import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import type { InboxMessage } from "../../src/inbox-v2/contract.ts";
import { replyRecipients } from "../../src/inbox-v2/reply-recipients.ts";
import { ReplyAllArrowIcon, ReplyArrowIcon } from "../icons.tsx";
import { MailAction } from "./message-actions.tsx";

const OPEN_REPLY_MESSAGE = "leadcue-open-reply-message";
type ReplySource = { messageId: string; selection: number; all: boolean };
const selectedReplies = new Map<string, ReplySource>();
const ReplySourceSchema = z.strictObject({
  workspaceId: z.string(),
  planId: z.string(),
  messageId: z.string().regex(/^bison:reply:[1-9]\d*$/u),
  all: z.boolean().default(false),
});

/** Asks the composer to reply to this message, to its sender or to everyone on it. */
export function openReply(workspaceId: string, planId: string, messageId: string, all: boolean) {
  window.dispatchEvent(
    new CustomEvent(OPEN_REPLY_MESSAGE, { detail: { workspaceId, planId, messageId, all } }),
  );
}

/** Whether a reply all here reaches anyone the plain reply does not. */
export function hasOthers(message: InboxMessage, mailbox: string | null | undefined): boolean {
  return (
    message.direction === "inbound" &&
    !!mailbox &&
    (replyRecipients(message, mailbox, true)?.cc.length ?? 0) > 0
  );
}

/** A sent reply still answers the lead; sequence messages wait for the parent probe. */
export function ReplyMessageAction(props: {
  workspaceId: string;
  planId: string;
  message: InboxMessage;
  mailbox?: string | null;
}) {
  if (!ReplySourceSchema.shape.messageId.safeParse(props.message.id).success) return null;
  const open = (all: boolean) => openReply(props.workspaceId, props.planId, props.message.id, all);
  return (
    <>
      <MailAction
        label={props.message.direction === "outbound" ? "Reply to lead" : "Reply"}
        icon={<ReplyArrowIcon size={18} />}
        onClick={() => open(false)}
      />
      {hasOthers(props.message, props.mailbox) && (
        <MailAction
          label="Reply all"
          icon={<ReplyAllArrowIcon size={18} />}
          onClick={() => open(true)}
        />
      )}
    </>
  );
}

/**
 * The message a per-message Reply chose, kept through remounts of the box; `reset` puts the box back
 * on its default, the lead (a send does it, so a past choice never sticks to the next reply).
 */
export function useReplyMessage(
  userId: string,
  workspaceId: string,
  planId: string,
  onOpen: () => void,
) {
  const key = [userId, workspaceId, planId].join("\u0000");
  const [source, setSource] = useState<ReplySource | null>(() => selectedReplies.get(key) ?? null);
  useEffect(() => {
    const open = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const parsed = ReplySourceSchema.safeParse(event.detail);
      if (
        !parsed.success ||
        parsed.data.workspaceId !== workspaceId ||
        parsed.data.planId !== planId
      )
        return;
      const selected = {
        messageId: parsed.data.messageId,
        all: parsed.data.all,
        selection: (selectedReplies.get(key)?.selection ?? 0) + 1,
      };
      selectedReplies.set(key, selected);
      setSource(selected);
      onOpen();
    };
    window.addEventListener(OPEN_REPLY_MESSAGE, open);
    return () => window.removeEventListener(OPEN_REPLY_MESSAGE, open);
  }, [key, workspaceId, planId, onOpen]);
  const reset = useCallback(() => {
    selectedReplies.delete(key);
    setSource(null);
  }, [key]);
  return [source, reset] as const;
}
